import { useState, useEffect } from "react";
import { Trophy, Users, Crown, Medal } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { apiGetUniLeaderboard, apiGetCrossUniLeaderboard, getUser } from "@/lib/storage";

export default function Leaderboard() {
  const [uniBoard, setUniBoard] = useState([]);
  const [crossBoard, setCrossBoard] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const myUni = user?.university || "UCL";

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [uni, cross] = await Promise.all([
        apiGetUniLeaderboard(myUni),
        apiGetCrossUniLeaderboard(),
      ]);
      setUniBoard(uni.leaderboard || []);
      setCrossBoard(cross.standings || []);
      setLoading(false);
    }
    load();
  }, [myUni]);

  const rankColors = ["text-yellow-400", "text-slate-300", "text-amber-600"];

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="leaderboard-page">
      <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>LEADERBOARD</h1>

      <Tabs defaultValue="university">
        <TabsList className="bg-white/5 h-auto p-0.5" data-testid="leaderboard-tabs">
          <TabsTrigger value="university" className="text-xs uppercase tracking-wider data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm px-4 py-1.5" data-testid="tab-university">
            My University ({myUni})
          </TabsTrigger>
          <TabsTrigger value="cross" className="text-xs uppercase tracking-wider data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-sm px-4 py-1.5" data-testid="tab-cross-uni">
            All Universities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="university" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500 text-xs">Loading...</div>
          ) : uniBoard.length === 0 ? (
            <div className="text-center py-12 bg-[#111827] border border-white/5 rounded-sm">
              <Trophy size={24} className="mx-auto text-slate-600 mb-2" />
              <p className="text-xs text-slate-500">No {myUni} players yet. Be the first!</p>
            </div>
          ) : (
            <div className="bg-[#111827] border border-white/5 rounded-sm overflow-hidden">
              <div className="red-gradient-line" />
              <div className="grid grid-cols-[50px_1fr_80px_80px] px-4 py-2 text-[10px] text-slate-600 uppercase tracking-widest border-b border-white/5">
                <span>Rank</span><span>Player</span><span className="text-right">Predictions</span><span className="text-right">Points</span>
              </div>
              {uniBoard.map((u, i) => (
                <div
                  key={u.id}
                  className={`grid grid-cols-[50px_1fr_80px_80px] px-4 py-3 items-center border-b border-white/[0.03] ${
                    u.id === user?.id ? "bg-red-500/5" : ""
                  }`}
                  data-testid={`uni-rank-${i + 1}`}
                >
                  <span className={`text-sm font-bold font-mono ${rankColors[i] || "text-slate-500"}`}>
                    {i < 3 ? ["1st", "2nd", "3rd"][i] : `${i + 1}th`}
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-white truncate">{u.username}</span>
                    {u.id === user?.id && <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400">You</Badge>}
                    {i === 0 && <Crown size={12} className="text-yellow-400 flex-shrink-0" />}
                  </div>
                  <span className="text-xs text-slate-400 font-mono text-right">{u.prediction_count || 0}</span>
                  <span className="text-xs font-bold font-mono text-red-400 text-right">{u.points}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cross" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500 text-xs">Loading...</div>
          ) : (
            <div className="space-y-3">
              {crossBoard.map((uni, i) => (
                <div
                  key={uni.university}
                  className={`bg-[#111827] border border-white/5 rounded-sm p-5 ${
                    uni.university === myUni ? "border-red-500/30 bg-red-500/5" : ""
                  }`}
                  data-testid={`cross-uni-${uni.university}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className={`text-2xl font-bold font-mono ${rankColors[i] || "text-slate-500"}`}>
                        #{i + 1}
                      </span>
                      <div>
                        <p className="text-sm font-bold text-white">{uni.university}</p>
                        {uni.university === myUni && (
                          <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400 mt-0.5">Your Uni</Badge>
                        )}
                      </div>
                    </div>
                    {i === 0 && <Medal size={20} className="text-yellow-400" />}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-[10px] text-slate-600 uppercase tracking-widest">Total Points</p>
                      <p className="text-lg font-bold font-mono text-red-400">{uni.total_points}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-600 uppercase tracking-widest">Members</p>
                      <p className="text-lg font-bold font-mono text-white">{uni.member_count}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-600 uppercase tracking-widest">Avg Points</p>
                      <p className="text-lg font-bold font-mono text-slate-400">{uni.avg_points}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
